import { useMemo } from 'react';
import { CardItem, CardItemLabel, CardItemContent, CardItemValue } from '../ui/details-card';
import Text from '../ui/text';
import { MapPin, Phone, SquareCheck, Info } from 'lucide-react'
import { formatPhoneNumber, getCodeLanguagesList } from '../../lib/utils';
import { Tooltip } from '@nextui-org/react';

const DoctorDetails = props => {
  const { data = {} } = props;
  const languages = getCodeLanguagesList(data?.languages);
  const locations = useMemo(() => {
    const list = Array.isArray(data?.locations) ? data.locations.slice() : [];
    return list.sort((a, b) => {
      const aHas = !!(a?.practitionerPCPID && String(a.practitionerPCPID).trim() !== '');
      const bHas = !!(b?.practitionerPCPID && String(b.practitionerPCPID).trim() !== '');
      if (aHas === bHas) return 0;
      return aHas ? -1 : 1;
    });
  }, [data?.locations]);

  return (
    <div className='w-full flex flex-col justify-center items-center gap-10'>
        <div className='w-full grid grid-cols-12 gap-5 gap-5 sm:gap-8'>
            <div className='col-span-12 pb-3 border-b border-slate-300 grid grid-cols-12 gap-5'>
                <div className='col-span-12 sm:col-span-6'>
                    <CardItem>
                        <CardItemLabel><Text>Name</Text></CardItemLabel>
                        <CardItemContent>
                            <CardItemValue className='text-2xl'>{`${data?.practitionerFirstName} ${data?.practitionerLastName}, ${data?.practitionerTitle}`}</CardItemValue>
                        </CardItemContent>
                    </CardItem>
                </div>
                <div className='col-span-12 sm:col-span-4 flex flex-row gap-5 justify-center items-center'>
                    <CardItem>
                        <CardItemContent>
                            <CardItemValue>
                                <div className='text-sm text-primary font-semibold bg-orange-100 px-4 py-1 rounded-full'><Text>{data?.gender === 'M' ? 'MALE' : 'FEMALE'}</Text></div>
                            </CardItemValue>
                        </CardItemContent>
                    </CardItem>
                </div>
                <div className='col-span-12 sm:col-span-2'>
                    <CardItem className='flex flex-col justify-center items-center'>
                        <CardItemLabel className='text-center'><Text>Accepting New Patients?</Text></CardItemLabel>
                        <CardItemContent >
                            <CardItemValue>{data?.providerAcceptsNewPatient === 'Y' ? <SquareCheck size={28} strokeWidth={1} className='text-green-600' /> : <Text>No</Text>}</CardItemValue>
                        </CardItemContent>
                    </CardItem>
                </div>
            </div>
            <div className='col-span-12 sm:col-span-6 pb-3 border-b border-slate-300'>
                <CardItem>
                    <CardItemLabel><Text>Specialty</Text></CardItemLabel>
                    <CardItemContent>
                        <CardItemValue>{data?.specialties?.length > 0 ? (
                            <ul className='flex flex-col gap-1'>
                                {data?.specialties.map((item) => <li>{item}</li>)}
                            </ul>
                        ) : null}</CardItemValue>
                    </CardItemContent>
                </CardItem>    
            </div>    
            <div className='col-span-12 sm:col-span-6 pb-3 border-b border-slate-300'>  
                <CardItem>
                    <CardItemLabel><Text>Group Affiliation</Text></CardItemLabel>
                    <CardItemContent>
                        <CardItemValue>{data?.payees?.length > 0 ? (
                            <ul className='flex flex-col gap-1'>
                                {data?.payees.map((item) => <li>{item}</li>)}
                            </ul>
                        ) : null}</CardItemValue>
                    </CardItemContent>
                </CardItem>
            </div> 
            <div className='col-span-12 sm:col-span-6 pb-3 border-b border-slate-300'> 
                <CardItem>
                    <CardItemLabel><Text>Office Location Phone Number</Text></CardItemLabel>
                    <CardItemContent className='flex flex-col gap-1'>
                        <CardItemValue>{data?.providerAddress1}{data?.providerAddress2 ? `, ${data?.providerAddress2}` : ''}</CardItemValue>
                        <CardItemValue>{data?.providerCity}, {data?.providerState} {data?.providerZip}</CardItemValue>
                    </CardItemContent>
                    <CardItemContent>
                        <div><Phone size={28} strokeWidth={1} className='text-primary'/></div>
                        <CardItemValue>{(data?.providerPhone ? <a href={`tel:${data.providerPhone}`} className='underline text-gray-800 hover:text-gray-800 font-normal'>{formatPhoneNumber(data?.providerPhone)}</a> : null)}</CardItemValue>
                    </CardItemContent>
                </CardItem>
            </div>    
            <div className='col-span-12 sm:col-span-6 pb-3 border-b border-slate-300'> 
                <CardItem>
                    <CardItemLabel><Text>Hospitals Affiliations</Text></CardItemLabel>
                    <CardItemContent>
                        <CardItemValue>{data?.hospitalPrivileges?.length > 0 ? (
                            <ul className='flex flex-col gap-1'>
                                {data?.hospitalPrivileges.map((item) => <li>{item.name}</li>)}
                            </ul>
                        ) : null}</CardItemValue>
                    </CardItemContent>
                </CardItem>
            </div>
            <div className='col-span-12 sm:col-span-6 pb-3 border-b border-slate-300'>     
                <CardItem>
                    <CardItemLabel>
                        <div className='flex flex-row gap-2 items-center'>
                            <Text>Provider ID Number</Text>
                            <Tooltip placement='right-end' content={<div className='text-sm max-w-xs'><Text>Providers in the same group may have different IDs based on their office location. Be sure to select the ID for the office you plan to visit.</Text></div>}>
                                <Info size={16} />
                            </Tooltip>
                        </div>
                    </CardItemLabel>
                    <CardItemContent>
                        <CardItemValue>{data?.practitionerPCPID}</CardItemValue>
                    </CardItemContent>
                </CardItem>
            </div>
            <div className='col-span-12 sm:col-span-6 pb-3 border-b border-slate-300'> 
                <CardItem>
                    <CardItemLabel><Text>Board Certification(s)</Text></CardItemLabel>
                    <CardItemContent>
                        <CardItemValue>{data?.boardCertifications?.length > 0 ? (
                            <ul className='flex flex-col gap-1'>
                                {data?.boardCertifications.map((item) => <li>{item.name}</li>)}
                            </ul>
                        ) : 'N/A'}</CardItemValue>
                    </CardItemContent>
                </CardItem>
            </div>
            <div className='col-span-12 sm:col-span-6 pb-3 border-b border-slate-300'> 
                <CardItem>
                    <CardItemLabel><Text>Languages</Text></CardItemLabel>
                    <CardItemContent>
                        <CardItemValue className=''>{languages.map(item => <div className='inline-block m-1 text-sm text-secondary font-semibold bg-gray-200 px-4 py-1 rounded-full'>{item}</div>)}</CardItemValue>
                    </CardItemContent>
                </CardItem>
            </div>     
        </div>
        <div className='w-full flex flex-row gap-5'>
            <CardItem className='w-full'>
                <CardItemLabel className='w-full text-start py-5'><Text>Other Locations</Text></CardItemLabel>
                <CardItemContent className='w-full'>
                    <CardItemValue className='w-full'>
                        <ul className='flex flex-col gap-2'>
                            {locations.map((item) => 
                            <li className='grid grid-cols-12 border-b border-slate-300 pb-3 gap-3'>
                                <div className='col-span-12 sm:col-span-4 flex flex-row justify-start items-center gap-3'>{item?.practitionerPCPID || ''}</div>
                                <div className={`col-span-12 sm:col-span-4 flex flex-row justify-start items-center gap-3`}>
                                    <MapPin size={28} strokeWidth={1} className='text-primary'/> 
                                    <div>
                                        <div>{item.address1}{item.address2 ? `, ${item.address2}` : ''}</div>
                                        <div>{item.city}{item.state || item.zip5 ? `, ` : ''}{item.state ? `${item.state}` : ''}{item.zip5 ? ` ${item.zip5}` : ''}</div>
                                    </div>
                                </div>
                                <div className={`col-span-12 sm:col-span-4 flex flex-row justify-start items-center gap-3`}>
                                    <Phone size={28} strokeWidth={1} className='text-primary'/> {(data?.providerPhone ? <a href={`tel:${data.providerPhone}`} className='underline text-gray-800 hover:text-gray-800 font-normal'>{formatPhoneNumber(data?.providerPhone)}</a> : null)}
                                </div>
                            </li>)}
                        </ul>
                    </CardItemValue>
                </CardItemContent>
            </CardItem>
        </div>
    </div>
    
  );
};

export default DoctorDetails;
